import * as React from "react"
import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable
} from "@tanstack/react-table"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table"
import { useState, useEffect } from "react";
import { ONE_TIME_CONTRACT_STATUS, PAGINATION_SETTINGS } from "@/constants";
import { COPYRIGHT_TYPES } from "@/constants/oneTimeContract";
import { getOneTimeContracts } from "@/service/weexpertService";
import { useTranslation } from 'react-i18next';
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';

import { DataTablePagination } from "@/components/ui/table-pagination"
import { Button } from "@/components/ui/button";
import { useDebounce } from "@/components/ui/multiple-selector";
import { useQuery } from "react-query";
import { useQueryState } from "./invoices.page";
import { format } from "date-fns";
import { OneTimeContractFilter, processOneTimeContractFilter } from "@/components/popups/one-time-contract.filter.popup";
import { getUser } from "@/service/weexpertService";
import { Skeleton } from "@radix-ui/themes";
import { Badge } from "@/components/ui/badge";
import FilterChips from "@/components/filter-chips/filter.chips";
import { useLocalStorageState } from "@/lib/useCookieState";


export function OneTimeContractsPage(props) {

  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState(null);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [sharedFilter, setSharedFilter] = useQueryState('f', 'one_time_contracts_filter')

  const [storedFilter, setStoredFilter] = useLocalStorageState('one_time_contract_filter');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(JSON.parse(sharedFilter ?? '{}').rowsPerPage ?? PAGINATION_SETTINGS.oneTimeContracts);
  const [pagination, setPagination] = useState({ pageIndex: page, pageSize: rowsPerPage });

  const [unprocessedFilter, setUnprocessedFilter] = useState(JSON.parse(storedFilter ? storedFilter : '{}') ?? {})

  const [filters, setFilters] = useState(processOneTimeContractFilter(unprocessedFilter) ?? []);

  const { data: contracts, isLoading, isFetched } = useQuery({
    queryKey: ['oneTimeContracts', page, rowsPerPage, debouncedSearchQuery, filters],
    queryFn: () => getOneTimeContracts(rowsPerPage, page * rowsPerPage, debouncedSearchQuery, Object.values(filters))
  })

  const { data: user, isLoading: isUserLoading, isFetched: userIsFetched } = useQuery({ queryKey: 'userMe', queryFn: getUser, retry: false })


  useEffect(() => {
    setPage(pagination.pageIndex)
    setRowsPerPage(pagination.pageSize)
    setSharedFilter(JSON.stringify({ page: pagination.pageIndex, rowsPerPage: pagination.pageSize }))
  }, [pagination])

  const columns = [
    {
      header: t("common:status"),
      cell: ({ row }) => {
        const { status } = row.original;
        const display = ((w) => {
          switch (w) {
            case ONE_TIME_CONTRACT_STATUS.DRAFT: {
              return t("contracts:draft");
            }
            case ONE_TIME_CONTRACT_STATUS.AWAITING_APPROVAL: {
              return t("contracts:awaiting_approval");
            }
            case ONE_TIME_CONTRACT_STATUS.ACTIVE: {
              return t("contracts:active");
            }
            case ONE_TIME_CONTRACT_STATUS.COMPLETED: {
              return t("contracts:completed");
            }
            case ONE_TIME_CONTRACT_STATUS.AWATING_PAYMENT: {
              return t("contracts:awaiting_payment");
            }
            case ONE_TIME_CONTRACT_STATUS.AWATING_SIGNATURE: {
              return t("contracts:awaiting_signature");
            }
            case ONE_TIME_CONTRACT_STATUS.STARTED_MONEY_WITHDRAW_PROCESS: {
              return t("contracts:started_money_withdraw_process");
            }
            case ONE_TIME_CONTRACT_STATUS.MONEY_SEND_TO_USED: {
              return t("contracts:money_send_to_used");
            }
            case ONE_TIME_CONTRACT_STATUS.WAITING_FOR_CHANGE: {
              return t("contracts:waiting_for_change");
            }
            case ONE_TIME_CONTRACT_STATUS.AWAITNG_USER_PAYOUT_REQUEST: {
              return t("contracts:awaiting_user_payout_request");
            }
            default: {
              return w;
            }
          }
        })(status)
        const cssClasses = ((w) => {
          switch (w) {
            case ONE_TIME_CONTRACT_STATUS.DRAFT: {
              return 'bg-gray-500 rounded text-white text-center px-3 py-1'
            }
            case ONE_TIME_CONTRACT_STATUS.AWAITING_APPROVAL: {
              return 'bg-amber-500 rounded text-white text-center px-3 py-1'
            }
            case ONE_TIME_CONTRACT_STATUS.ACTIVE: {
              return 'bg-emerald-600 rounded text-white text-center px-3 py-1'
            }
            case ONE_TIME_CONTRACT_STATUS.COMPLETED: {
              return 'bg-blue-600 rounded text-white text-center px-3 py-1'
            }
            case ONE_TIME_CONTRACT_STATUS.AWATING_PAYMENT: {
              return 'bg-orange-500 rounded text-white text-center px-3 py-1'
            }
            case ONE_TIME_CONTRACT_STATUS.AWATING_SIGNATURE: {
              return 'bg-indigo-500 rounded text-white text-center px-3 py-1'
            }
            case ONE_TIME_CONTRACT_STATUS.STARTED_MONEY_WITHDRAW_PROCESS: {
              return 'bg-purple-600 rounded text-white text-center px-3 py-1'
            }
            case ONE_TIME_CONTRACT_STATUS.MONEY_SEND_TO_USED: {
              return 'bg-green-600 rounded text-white text-center px-3 py-1'
            }
            case ONE_TIME_CONTRACT_STATUS.WAITING_FOR_CHANGE: {
              return 'bg-yellow-500 rounded text-white text-center px-3 py-1'
            }
            case ONE_TIME_CONTRACT_STATUS.AWAITNG_USER_PAYOUT_REQUEST: {
              return 'bg-amber-600 rounded text-white text-center px-3 py-1'
            }
            default: {
              return 'bg-pirpleLightBg rounded text-pirpleText text-center px-3 py-1'
            }
          }
          })(status)
          return (
          <div className="flex items-center gap-2">
            <div className={`capitalize ${cssClasses}`}>
              {display}
            </div>
            {user?.data?.roles === 'CLIENT' && status === 'AWAITNG_USER_PAYOUT_REQUEST' && (
              <span className="text-orange-600" title="Wymagana akcja">⚠️</span>
            )}
          </div>
        )
      },
      key: uuidv4()
    },
    {
      accessorKey: "contractNumber",
      header: t("contracts:contract_number"),
      cell: ({ row }) => {
        return <Link className="capitalize" to={`/contracts/one-time/${row.original.id}`}>
          {`${row.original.contractNumber}`}
        </Link>
      },
      key: uuidv4()
    },
    // Only show client column if user is NOT a client
    ...(user?.data?.roles !== 'CLIENT' ? [{
      accessorKey: "client",
      header: t("common:client"),
      cell: ({ row }) => (
        <Link className="capitalize" to={`/contracts/one-time/${row.original.id}`}>
          {row.original.client ? `${row.original.client.firstName} ${row.original.client.lastName}` : '-'}
        </Link>
      ),
      key: uuidv4()
    }] : []),
    {
      accessorKey: "counterparty",
      header: t("counterparty"),
      cell: ({ row }) => (
        <Link className="capitalize" to={`/contracts/one-time/${row.original.id}`}>
          {row.original.counterparty ? row.original.counterparty.name : '-'}
        </Link>
      ),
      key: uuidv4()
    },
    {
      header: t("contracts:copyright_type"),
      cell: ({ row }) => {
        const getCopyrightBadge = (copyrightType) => {
          const copyrightInfo = Object.values(COPYRIGHT_TYPES).find(type => type.id === copyrightType);
          const label = copyrightInfo ? copyrightInfo.label : copyrightType;

          switch (copyrightType) {
            case 'LICENSE':
              return <Badge className={"bg-emerald-600"}>{label}</Badge>
            case 'COPYRIGHT':
              return <Badge className={"bg-blue-600"}>{label}</Badge>
            case 'NO_TRANSFER':
              return <Badge className={"bg-gray-600"}>{label}</Badge>
            default: {
              return <Badge>{label}</Badge>
            }
          }
        }
        return getCopyrightBadge(row.original.copyrightType)
      },
      key: uuidv4()
    },
    {
      header: t("bills:netto_amount"),
      cell: ({ row }) => (
        <Link className="capitalize" to={`/contracts/one-time/${row.original.id}`}>
          {row.original.priceNetto ? `${parseFloat(row.original.priceNetto).toFixed(2)} ${row.original.currency || 'PLN'}` : `0.00 ${row.original.currency || 'PLN'}`}
        </Link>
      ),
      key: uuidv4()
    },
    {
      header: t("contracts:payment_due_date"),
      cell: ({ row }) => (
        <Link className="capitalize" to={`/contracts/one-time/${row.original.id}`}>
          {row.original.paymentDueDate ? format(new Date(row.original.paymentDueDate), 'dd.MM.yyyy') : '-'}
        </Link>
      ),
      key: uuidv4()
    },
    {
      header: t("common:created_at"),
      cell: ({ row }) => (
        <Link className="capitalize" to={`/contracts/one-time/${row.original.id}`}>
          {format(new Date(row.original.createdAt ?? null), 'dd.MM.yyyy')}
        </Link>
      ),
      key: uuidv4()
    }
  ]

  const table = useReactTable({
    data: isLoading ? Array(rowsPerPage).fill({}) : contracts?.data?.data?.contracts ?? [],
    columns: isLoading ? columns.map(column => {
      return {
        ...column,
        cell: () => <Skeleton className={"h-4 w-[150px]"} />
      }
    }) : columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
    rowCount: contracts?.data?.data?.total ?? 0,
    state: {
      pagination
    },
    initialState: {
      pagination
    }
  });

  useEffect(() => {
    setStoredFilter(unprocessedFilter);
    setFilters(processOneTimeContractFilter(unprocessedFilter));
  }, [unprocessedFilter])

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchQuery]);

  return (
    <>
      <div className="relative">
        <div className="sticky left-0 top-16 z-10 w-full bg-grayLightMainBg">
          <div className="flex justify-between py-8 pr-8 items-center bg-grayLightMainBg">
            <div className="ml-8 text-4xl ">{t("contracts:one_time_contracts")}</div>
            <div className="flex w-5/6">
              <Input
                placeholder={t("search")}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="mr-2 ml-8 bg-white"
              />
            </div>
            {/* Filter hidden */}
            {!isUserLoading && user.data.roles === 'CLIENT' && (
              <Link to='/contracts/one-time/create'>
                <Button className="mr-2 text-blueText bg-blueLightAccent hover:bg-blueLightAccent border-[1px] border-blueBorder shadow-none">
                  {t("button_create")}
                </Button>
              </Link>
            )}
          </div>
        </div>
        {/* Filter chips hidden */}

        <div className="w-full px-8 py-4 bg-grayLightMainBg">
          <div className="rounded-md border">
            {!isLoading && (
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map(headerGroup => (
                    <TableRow key={uuidv4()}>
                      {headerGroup.headers.map(header => {
                        return (
                          <TableHead className="pt-3 pb-3"
                            key={uuidv4()}>
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                          </TableHead>
                        )
                      })}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows?.length ? (
                    table.getRowModel().rows.map(row => (
                      <TableRow
                        key={row.id}
                        data-state={row.getIsSelected() && "selected"}
                      >
                        {row.getVisibleCells().map(cell => (
                          <TableCell className="relative text-sm pt-3 pb-3"
                            key={uuidv4()}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="h-24 text-center m-[0px]"
                      >
                        {t("no_results")}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </div>
          <DataTablePagination table={table} />
        </div>
      </div>
    </>
  )
}
